export function emptyManifest() {
    return {
        items: new Map(),
        tags: new Map(),
        categories: new Map(),
        locations: new Map(),
        customFieldDefs: new Map(),
        investments: new Map(),
        seen: new Set(),
    };
}
const APPLIED = { status: 'applied', superseded: [] };
const DUPLICATE = { status: 'duplicate', superseded: [] };
// ---------------------------------------------------------------------------
// Primitives
// ---------------------------------------------------------------------------
/** Strictly newer by the total order. Equal clocks cannot occur for distinct writes: deviceId breaks the tie. */
const isNewer = (incoming, stored) => stored === null || stored === undefined || incoming > stored;
/** Per-field LWW. Returns the cell to keep. */
function lww(stored, hlc, v) {
    return isNewer(hlc, stored?.hlc) ? { v, hlc } : stored;
}
function decide(stored, hlc, present) {
    return isNewer(hlc, stored?.hlc) ? { present, hlc } : stored;
}
function entity(collection, id) {
    let record = collection.get(id);
    if (!record) {
        record = { entityId: id, fields: new Map(), lifecycle: null, links: new Map() };
        collection.set(id, record);
    }
    return record;
}
/** Applies each key of a payload as an independently clocked field. */
function upsert(record, hlc, payload) {
    for (const [key, value] of Object.entries(payload)) {
        record.fields.set(key, lww(record.fields.get(key), hlc, value));
    }
}
/**
 * Finds or creates an item.
 *
 * A field edit for an item whose creation event has not been applied yet creates
 * the record with `createdHlc: null`. Ignoring such an edit instead would make the
 * result depend on arrival order, which is precisely what FR-10.6 forbids -- and
 * ordered delivery is a property of the current protocol, not something the merge
 * function should assume.
 */
function ensureItem(m, itemId) {
    let item = m.items.get(itemId);
    if (!item) {
        item = {
            itemId,
            createdHlc: null,
            fields: {},
            customFields: new Map(),
            lifecycle: null,
            release: null,
            releaseDecision: null,
            intentions: new Map(),
            tags: new Map(),
            photos: new Map(),
            photoOrder: null,
            primaryPhotoId: null,
            currentIntention: null,
            reviewAgainOn: null,
        };
        m.items.set(itemId, item);
    }
    return item;
}
// ---------------------------------------------------------------------------
// Derived fields
// ---------------------------------------------------------------------------
/**
 * Recomputes the denormalized fields from their append-only sources.
 *
 * Recomputed rather than merged, which is why they cannot conflict: two devices
 * recording different intentions both append rows, and both devices then derive
 * the same current intention from the row with the greatest clock.
 */
function recomputeDerived(item) {
    let latest = null;
    for (const record of item.intentions.values()) {
        if (latest === null || record.hlc > latest.hlc)
            latest = record;
    }
    item.currentIntention = latest?.intention ?? null;
    item.reviewAgainOn = latest?.reviewAgainOn ?? null;
}
// ---------------------------------------------------------------------------
// Apply
// ---------------------------------------------------------------------------
/**
 * Applies one event to the manifest, in place, and reports what happened.
 *
 * Mutates rather than copying: both real stores mutate (DynamoDB `UpdateItem`,
 * Dexie `put`), and a fold over tens of thousands of events during bootstrap
 * cannot afford to rebuild the manifest each step. Determinism comes from clock
 * comparison, not from immutability.
 */
export function applyEvent(m, e) {
    if (m.seen.has(e.eventId))
        return DUPLICATE;
    const result = projectEvent(m, e);
    m.seen.add(e.eventId);
    return result;
}
/**
 * Applies an event without consulting the in-memory `seen` set.
 *
 * For the server, whose idempotency guarantee is the conditional write of the
 * `SEEN#<eventId>` marker inside the same transaction as the projection update
 * (data model §2.3) -- a durable check that outlives any one Lambda invocation,
 * which an in-process set cannot be. Clients should call `applyEvent`.
 *
 * Safe to call twice regardless: every decision is a clock comparison, so
 * re-applying an event reaches the same state.
 */
export function projectEvent(m, e) {
    switch (e.type) {
        // --- items -----------------------------------------------------------
        case 'item.created': {
            const item = ensureItem(m, e.entityId);
            // The earliest creation clock wins, so the value does not depend on which
            // creation event was applied first. In practice there is only ever one.
            if (item.createdHlc === null || e.hlc < item.createdHlc)
                item.createdHlc = e.hlc;
            const superseded = [];
            for (const [field, value] of Object.entries(e.payload)) {
                if (value === undefined)
                    continue;
                const loss = setField(item, field, value, e.hlc, e.eventId);
                if (loss)
                    superseded.push(loss);
            }
            return superseded.length > 0 ? { status: 'superseded', superseded } : APPLIED;
        }
        case 'item.fieldSet': {
            const item = ensureItem(m, e.entityId);
            const loss = setField(item, e.payload.field, e.payload.value, e.hlc, e.eventId);
            // The only field that feeds a derived value, so the only one that has to
            // trigger a recompute.
            return loss ? { status: 'superseded', superseded: [loss] } : APPLIED;
        }
        case 'item.customFieldSet': {
            const item = ensureItem(m, e.entityId);
            const { fieldId, value } = e.payload;
            const stored = item.customFields.get(fieldId);
            if (!isNewer(e.hlc, stored?.hlc)) {
                return {
                    status: 'superseded',
                    superseded: [
                        {
                            eventId: e.eventId,
                            itemId: e.entityId,
                            field: `custom:${fieldId}`,
                            yourValue: value,
                            winningValue: stored.v,
                            winningHlc: stored.hlc,
                        },
                    ],
                };
            }
            item.customFields.set(fieldId, { v: value, hlc: e.hlc });
            return APPLIED;
        }
        /**
         * Soft, with a 30-day recovery window (FR-3.8). Deliberately independent of
         * the field cells: correctness case 7 requires that a concurrent edit remain
         * recoverable, so a delete hides the item without touching what was written.
         */
        case 'item.deleted':
        case 'item.restored': {
            const item = ensureItem(m, e.entityId);
            item.lifecycle = decide(item.lifecycle, e.hlc, e.type === 'item.restored');
            return APPLIED;
        }
        // --- intention -------------------------------------------------------
        case 'intention.set': {
            const item = ensureItem(m, e.entityId);
            // Keyed by clock, so re-applying writes an identical row rather than a second one.
            item.intentions.set(e.hlc, {
                hlc: e.hlc,
                intention: e.payload.intention,
                at: e.clientTs,
                ...(e.payload.reason === undefined ? {} : { reason: e.payload.reason }),
                ...(e.payload.reviewAgainOn === undefined
                    ? {}
                    : { reviewAgainOn: e.payload.reviewAgainOn }),
            });
            recomputeDerived(item);
            return APPLIED;
        }
        // --- uses, no longer kept -------------------------------------------
        /*
         * Recorded uses, which the application no longer keeps.
         *
         * Accepted and ignored rather than rejected or removed from the schema. The
         * log is append-only and these events are in it: a device that has not
         * synced since they were written will push them, and a bootstrap will read
         * them back. Refusing them would strand that device, and dropping them from
         * the schema would make the whole batch fail to parse rather than this one
         * event fail to matter.
         */
        case 'use.recorded':
        case 'use.deleted':
            return APPLIED;
        // --- release ---------------------------------------------------------
        case 'release.recorded': {
            const item = ensureItem(m, e.entityId);
            item.releaseDecision = decide(item.releaseDecision, e.hlc, true);
            item.release = lww(item.release, e.hlc, e.payload);
            return APPLIED;
        }
        case 'release.cleared': {
            const item = ensureItem(m, e.entityId);
            item.releaseDecision = decide(item.releaseDecision, e.hlc, false);
            return APPLIED;
        }
        // --- tags ------------------------------------------------------------
        case 'tag.created': {
            const tag = ensureTag(m, e.entityId);
            tag.displayName = lww(tag.displayName, e.hlc, e.payload.displayName);
            tag.normalizedName = lww(tag.normalizedName, e.hlc, e.payload.normalizedName);
            if (e.payload.color !== undefined) {
                tag.color = lww(tag.color, e.hlc, e.payload.color);
            }
            return APPLIED;
        }
        /**
         * Changes the display name only. `tagId` is the envelope's `entityId` and is
         * untouched, so a historical count of "tee-shirt" stays correct after the tag
         * is renamed to "t-shirt" (FR-9.10, correctness case 8).
         */
        case 'tag.renamed': {
            const tag = ensureTag(m, e.entityId);
            tag.displayName = lww(tag.displayName, e.hlc, e.payload.displayName);
            tag.normalizedName = lww(tag.normalizedName, e.hlc, e.payload.normalizedName);
            return APPLIED;
        }
        case 'tag.deleted': {
            const tag = ensureTag(m, e.entityId);
            tag.lifecycle = decide(tag.lifecycle, e.hlc, false);
            return APPLIED;
        }
        case 'tag.merged': {
            const tag = ensureTag(m, e.entityId);
            tag.mergedInto = lww(tag.mergedInto, e.hlc, e.payload.intoTagId);
            return APPLIED;
        }
        case 'tag.applied':
        case 'tag.removed': {
            const item = ensureItem(m, e.payload.itemId);
            const { tagId } = e.payload;
            item.tags.set(tagId, decide(item.tags.get(tagId) ?? null, e.hlc, e.type === 'tag.applied'));
            return APPLIED;
        }
        // --- photos ----------------------------------------------------------
        case 'photo.added': {
            const photo = ensurePhoto(m, e.entityId, e.payload.photoId);
            photo.metadata = lww(photo.metadata, e.hlc, {
                originalKey: e.payload.originalKey,
                derivativeKey: e.payload.derivativeKey,
                width: e.payload.width,
                height: e.payload.height,
                byteSize: e.payload.byteSize,
            });
            photo.lifecycle = decide(photo.lifecycle, e.hlc, true);
            return APPLIED;
        }
        case 'photo.uploaded': {
            const photo = ensurePhoto(m, e.entityId, e.payload.photoId);
            photo.uploaded = lww(photo.uploaded, e.hlc, true);
            return APPLIED;
        }
        case 'photo.removed': {
            const photo = ensurePhoto(m, e.entityId, e.payload.photoId);
            photo.lifecycle = decide(photo.lifecycle, e.hlc, false);
            return APPLIED;
        }
        /** A full ordering rather than a pairwise swap, so the result cannot depend on arrival order. */
        case 'photo.reordered': {
            const item = ensureItem(m, e.entityId);
            item.photoOrder = lww(item.photoOrder, e.hlc, e.payload.photoIds);
            return APPLIED;
        }
        case 'photo.primarySet': {
            const item = ensureItem(m, e.entityId);
            item.primaryPhotoId = lww(item.primaryPhotoId, e.hlc, e.payload.photoId);
            return APPLIED;
        }
        // --- categories, locations, custom field definitions, investments ----
        case 'category.created':
        case 'category.updated':
            upsert(entity(m.categories, e.entityId), e.hlc, e.payload);
            return APPLIED;
        case 'category.deleted': {
            const record = entity(m.categories, e.entityId);
            record.lifecycle = decide(record.lifecycle, e.hlc, false);
            return APPLIED;
        }
        case 'location.created':
        case 'location.updated':
            upsert(entity(m.locations, e.entityId), e.hlc, e.payload);
            return APPLIED;
        case 'location.deleted': {
            const record = entity(m.locations, e.entityId);
            record.lifecycle = decide(record.lifecycle, e.hlc, false);
            return APPLIED;
        }
        case 'field.defined':
        case 'field.updated':
            upsert(entity(m.customFieldDefs, e.entityId), e.hlc, e.payload);
            return APPLIED;
        case 'field.deleted': {
            const record = entity(m.customFieldDefs, e.entityId);
            record.lifecycle = decide(record.lifecycle, e.hlc, false);
            return APPLIED;
        }
        case 'investment.created':
        case 'investment.updated':
        case 'investment.acquired':
        case 'investment.abandoned':
            upsert(entity(m.investments, e.entityId), e.hlc, e.payload);
            return APPLIED;
        case 'investment.linked':
        case 'investment.unlinked': {
            const record = entity(m.investments, e.entityId);
            const { itemId } = e.payload;
            record.links.set(itemId, decide(record.links.get(itemId) ?? null, e.hlc, e.type === 'investment.linked'));
            return APPLIED;
        }
        case 'investment.deleted': {
            const record = entity(m.investments, e.entityId);
            record.lifecycle = decide(record.lifecycle, e.hlc, false);
            return APPLIED;
        }
    }
}
function ensureTag(m, tagId) {
    let tag = m.tags.get(tagId);
    if (!tag) {
        tag = {
            tagId,
            displayName: null,
            normalizedName: null,
            color: null,
            lifecycle: null,
            mergedInto: null,
        };
        m.tags.set(tagId, tag);
    }
    return tag;
}
function ensurePhoto(m, itemId, photoId) {
    const item = ensureItem(m, itemId);
    let photo = item.photos.get(photoId);
    if (!photo) {
        photo = { photoId, metadata: null, uploaded: null, lifecycle: null };
        item.photos.set(photoId, photo);
    }
    return photo;
}
/**
 * Applies one field, reporting the loss if the stored clock is greater.
 *
 * The event is still part of the log either way -- this function decides only
 * what the projection shows. The losing value is never destroyed (NFR-REL-5).
 */
/**
 * Field names that were renamed, mapped to what they are now.
 *
 * Applied here, as the single point every field write passes through, which is
 * what makes a rename cost one line instead of a migration. `item.created` and
 * `item.fieldSet` both arrive via `setField`, so an old event lands in the new
 * cell carrying its own clock -- and last-writer-wins then resolves old and new
 * names against each other for free, because they are the same cell.
 *
 * That is why this needed no compensating events, unlike `targetPrice` on an
 * investment entry (FR-8.1). There, the name was an object key in a payload
 * merged wholesale, so there was no interception point and the two names had to
 * be reconciled by every reader. Here the name arrives as a *value*, so it can
 * be translated once on the way in.
 *
 * Nothing downstream knows this field ever had two names: the search index, the
 * CSV columns, the filters and the capture form all see `color` only.
 */
const FIELD_ALIASES = {
    // `colour` became `color` on 2026-10-09.
    colour: 'color',
};
function setField(item, field, value, hlc, eventId) {
    const name = FIELD_ALIASES[field] ?? field;
    const fields = item.fields;
    const stored = fields[name];
    if (isNewer(hlc, stored?.hlc)) {
        fields[name] = { v: value, hlc };
        return null;
    }
    return {
        eventId,
        itemId: item.itemId,
        // The current name, not the one the event used: a superseded-field notice
        // names a field the user can go and look at.
        field: name,
        yourValue: value,
        winningValue: stored.v,
        winningHlc: stored.hlc,
    };
}
/**
 * Applies a batch, collecting everything the caller has to report.
 *
 * Order-independent by construction, so the caller may pass events in clock order
 * (as push and pull both do) or in any other -- the resulting manifest is the same.
 */
export function fold(events, into = emptyManifest()) {
    const superseded = [];
    const duplicates = [];
    for (const e of events) {
        const result = applyEvent(into, e);
        if (result.status === 'duplicate')
            duplicates.push(e.eventId);
        superseded.push(...result.superseded);
    }
    return { manifest: into, superseded, duplicates };
}
/**
 * Which record an event changes.
 *
 * Both stores need this to know what to write back, and it is knowledge about
 * events rather than about either store -- so it lives here rather than being
 * worked out twice. The subtlety it hides is that `entityId` is not always the
 * answer: `tag.applied` names a tag but changes the *item* that carries it.
 */
export function affectedBy(event) {
    switch (event.type) {
        case 'tag.applied':
        case 'tag.removed':
            return { kind: 'item', id: event.payload.itemId };
        case 'tag.created':
        case 'tag.renamed':
        case 'tag.deleted':
        case 'tag.merged':
            return { kind: 'tag', id: event.entityId };
        case 'category.created':
        case 'category.updated':
        case 'category.deleted':
            return { kind: 'category', id: event.entityId };
        case 'location.created':
        case 'location.updated':
        case 'location.deleted':
            return { kind: 'location', id: event.entityId };
        case 'field.defined':
        case 'field.updated':
        case 'field.deleted':
            return { kind: 'customField', id: event.entityId };
        case 'investment.created':
        case 'investment.updated':
        case 'investment.linked':
        case 'investment.unlinked':
        case 'investment.acquired':
        case 'investment.abandoned':
        case 'investment.deleted':
            return { kind: 'investment', id: event.entityId };
        default:
            // Everything else -- items, intentions, photos, releases -- hangs off
            // an item, and the envelope names it.
            return { kind: 'item', id: event.entityId };
    }
}
/** Current value of a field, or undefined if never set. */
export function fieldValue(item, field) {
    return item.fields[field]?.v;
}
/** Soft-deleted: hidden from views, restorable for 30 days (FR-3.8). */
export const isDeleted = (item) => item.lifecycle?.present === false;
/** Archived: has a release outcome, so excluded from active views and counts (FR-7.4). */
export const isArchived = (item) => item.releaseDecision?.present === true;
/** Photos still attached, in the order the item records, removals excluded. */
export function orderedPhotos(item) {
    const present = [...item.photos.values()].filter((photo) => photo.lifecycle?.present === true);
    const order = item.photoOrder?.v;
    if (order === undefined)
        return present;
    // A photo missing from the ordering sorts last rather than disappearing: the
    // ordering can lag behind an addition from another device.
    const position = (photoId) => {
        const at = order.indexOf(photoId);
        return at === -1 ? Number.MAX_SAFE_INTEGER : at;
    };
    return present.sort((a, b) => position(a.photoId) - position(b.photoId));
}
/**
 * The photo that represents an item (FR-2.4).
 *
 * Falls back to the first attached photo when the flag names one that has been
 * removed, or when it has not arrived yet -- an item created on another device
 * may be pulled before its `photo.primarySet`. Without the fallback an item would
 * show no photo at all despite having several.
 */
export function primaryPhoto(item) {
    const photos = orderedPhotos(item);
    const named = item.primaryPhotoId?.v;
    return photos.find((photo) => photo.photoId === named) ?? photos[0];
}
/** Tag ids currently applied to an item. */
export function appliedTagIds(item) {
    return [...item.tags.entries()].filter(([, t]) => t.present).map(([tagId]) => tagId);
}
/**
 * Follows a tag's merge chain to the tag that absorbed it.
 *
 * Data model §4 describes `tag.merged` as re-pointing the `ITAG#` rows. Resolving
 * at read time instead keeps the merge order-independent -- a `tag.applied` with a
 * higher clock than the merge still lands on the surviving tag -- and leaves the
 * original rows intact as history. Guarded against a cycle, which a pair of
 * concurrent merges on two devices could otherwise produce.
 */
export function resolveTagId(m, tagId) {
    const seen = new Set();
    let current = tagId;
    for (;;) {
        if (seen.has(current))
            return current;
        seen.add(current);
        const next = m.tags.get(current)?.mergedInto?.v;
        if (next === undefined || next === current)
            return current;
        current = next;
    }
}
/**
 * Items counted as part of the active manifest: created, not deleted, not archived.
 *
 * Counting is deliberately a function over the projection rather than a stored
 * total, because a stored total is one more thing two devices can disagree about.
 */
export function activeItems(m) {
    return [...m.items.values()].filter((i) => !isDeleted(i) && !isArchived(i));
}
/*
 * ---------------------------------------------------------------------------
 * Amendments this module makes to docs/design/02-data-model.md
 * ---------------------------------------------------------------------------
 *
 * Each follows from FR-10.6's requirement that merge be deterministic regardless
 * of arrival order. None is a preference; without them the same set of events can
 * fold to different manifests.
 *
 *  1. `ITAG#`, `USE#`, and `PHOTO#` removals are clock-stamped tombstones rather
 *     than absent rows. §2.1's "presence means applied" cannot decide between a
 *     removal and a later re-application.
 *
 *  2. Delete/restore and release-recorded/cleared each carry the clock of the
 *     latest decision. §3's `deletedHlc` alone cannot tell whether a restore is
 *     newer than the delete it would undo.
 *
 *  3. `tag.merged` is resolved at read time rather than by re-pointing `ITAG#`
 *     rows, so that a tag application concurrent with a merge is not lost.
 *
 *  4. Every photo event's `entityId` is the item, and a photo's metadata, upload
 *     state, and lifecycle each carry their own clock. An upload or removal that
 *     is applied before the corresponding `photo.added` would otherwise have no
 *     record to attach to and would be dropped.
 *
 * A projection row may therefore exist in a tombstoned state. The log remains the
 * source of truth and is never rewritten, so §1 is untouched.
 */
/**
 * Flagged as protected (NFR-SAFE-3).
 *
 * The requirement is about sentimental belongings: the ones whose value is not
 * their cost, and which a sweep across a filtered list could discard in a moment
 * of tidiness. The flag is what makes "be careful with this" a property of the
 * record rather than of the user's attention.
 *
 * Absent means unprotected. A flag nobody has set is not a decision, and
 * defaulting to protected would make the word meaningless.
 */
export const isProtected = (item) => item.fields.protected?.v === true;
/**
 * Picked out by the owner.
 *
 * Absent means not, like `protected` above: a flag nobody has set is not a
 * decision. Unlike `protected` it guards nothing -- it is there to be filtered
 * on, and carries no meaning the rest of the system is allowed to infer.
 */
export const isFavorite = (item) => item.fields.favorite?.v === true;
//# sourceMappingURL=merge.js.map